import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UsersIcon, ActivityIcon, TrendingUpIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface RealTimeStatsProps {
  snapId?: string;
}

export default function RealTimeStats({ snapId }: RealTimeStatsProps) {
  const [activeUsers, setActiveUsers] = useState(0);
  const [recentVotes, setRecentVotes] = useState(0);
  const [votingTrend, setVotingTrend] = useState(0);

  useEffect(() => {
    if (!snapId) return;

    // Initialize presence channel for this SNAP
    const channel = supabase.channel(`snap:${snapId}`, {
      config: {
        presence: {
          key: crypto.randomUUID(),
        },
      },
    });

    // Track user presence
    channel
      .on('presence', { event: 'sync' }, () => {
        const presenceState = channel.presenceState();
        const userCount = Object.keys(presenceState).length;
        setActiveUsers(userCount);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Enter the channel to mark user as present
          await channel.track({ online_at: new Date().toISOString() });
        }
      });

    // Get initial vote count and set up vote tracking
    const getInitialVotes = async () => {
      const { count } = await supabase
        .from('votes')
        .select('*', { count: 'exact', head: true })
        .eq('snap_id', snapId)
        .gte('created_at', new Date(Date.now() - 15 * 60 * 1000).toISOString()); // Last 15 minutes
      
      setRecentVotes(count || 0);
      setVotingTrend(Math.min(((count || 0) / 10) * 100, 100)); // Scale trend based on recent votes
    };

    getInitialVotes();

    // Subscribe to real-time vote updates
    const voteSubscription = supabase
      .channel('votes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'votes',
          filter: `snap_id=eq.${snapId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setRecentVotes((prev) => {
              const newCount = prev + 1;
              // Update trend based on vote velocity
              setVotingTrend((prevTrend) => {
                const increase = Math.min(prevTrend + 20, 100); // Increase trend more significantly
                return increase;
              });
              return newCount;
            });
          }
        }
      )
      .subscribe();

    // Gradually decrease voting trend if no new votes
    const trendInterval = setInterval(() => {
      setVotingTrend((prev) => {
        // Decrease trend more slowly when it's higher
        const decrease = prev > 50 ? 2 : 5;
        return Math.max(0, prev - decrease);
      });
    }, 10000);

    // Cleanup subscriptions and intervals
    return () => {
      channel.unsubscribe();
      voteSubscription.unsubscribe();
      clearInterval(trendInterval);
    };
  }, [snapId]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {/* Active Users */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <UsersIcon className="h-5 w-5 text-blue-500 mr-2" />
            <span className="text-sm font-medium text-gray-600">Active Users</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeUsers}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-lg font-bold text-gray-900"
            >
              {activeUsers}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-2">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-blue-500"
              initial={{ width: '0%' }}
              animate={{ width: `${(activeUsers / 50) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Recent Votes */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ActivityIcon className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-sm font-medium text-gray-600">Recent Votes</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={recentVotes}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-lg font-bold text-gray-900"
            >
              {recentVotes}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-2">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-green-500"
              initial={{ width: '0%' }}
              animate={{ width: `${Math.min((recentVotes / 100) * 100, 100)}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>

      {/* Voting Trend */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <TrendingUpIcon className="h-5 w-5 text-orange-500 mr-2" />
            <span className="text-sm font-medium text-gray-600">Voting Trend</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={votingTrend}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-lg font-bold text-gray-900"
            >
              {Math.round(votingTrend)}%
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-2">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-orange-500"
              initial={{ width: '0%' }}
              animate={{ width: `${votingTrend}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}